/**
 * Shared chapter-directory row template for the #chapterDirectoryModal list.
 * Both render paths — the SSR chapter view (renderChapterView) and the SPA
 * controller (chapterDirectoryController) — emit the exact same markup so
 * hydration repaints are pixel-identical and the SSR rows stay the SEO
 * carrier for every chapter link.
 */
import { escapeHtml } from '../server/services/seo/seoEscape';
import { resolveChapterRowSubtitle } from './chapterRowSubtitle';
export interface ChapterDirectoryRowData {
	index: number;
	title: string;
	subtitle?: string;
	summary?: string;
	duration?: string;
	locked?: boolean;
	hasAudio?: boolean;
	hasVideo?: boolean;
}

const STATE_PLAY_SVG =
	'<svg class="chapter-directory-play-glyph" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3"></polygon></svg>';
const STATE_LOCK_SVG =
	'<svg class="chapter-directory-lock" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="9" rx="2"></rect><path d="M8 11V8a4 4 0 0 1 8 0v3"></path></svg>';

function durationMarkup(value: string | undefined, extraClass: string): string {
	return value ? `<span class="chapter-directory-duration${extraClass}">${escapeHtml(value)}</span>` : '';
}

/**
 * Directory row built on the topic page's chapter module (article-item
 * grammar, prototype chapter-pc 2026-09-09 R9): number disc, title over the
 * authored chapter subtitle, trailing mono duration, and a trailing bare
 * play / lock glyph — no media badges, no circled state disc. The phone
 * sheet keeps the round-4 compact row by hiding the subtitle and the state
 * glyph in CSS; the inline duration span feeds the phone's title+duration
 * baseline while the trail copy feeds the PC's right-hand mono column.
 */
export function chapterDirectoryRowMarkup(slug: string, item: ChapterDirectoryRowData, currentIndex: number): string {
	const current = item.index === currentIndex;
	const locked = item.locked === true;
	const resolved = resolveChapterRowSubtitle(item);
	const subtitle = resolved.source !== 'fallback' ? `<span class="chapter-directory-subtitle">${escapeHtml(resolved.text)}</span>` : '';
	return `<a class="chapter-directory-row${current ? ' is-current' : ''}${locked ? ' is-locked' : ''}" href="/topics/${encodeURIComponent(slug)}/chapters/${item.index}" data-proto-click="showArticle(${item.index})"${current ? ' aria-current="page"' : ''}><span class="chapter-directory-index">${item.index}</span><span class="chapter-directory-copy"><span class="chapter-directory-title">${escapeHtml(item.title)}</span>${subtitle}</span>${durationMarkup(item.duration, ' chapter-directory-duration--inline')}${durationMarkup(item.duration, ' chapter-directory-duration--trail')}<span class="chapter-directory-state">${locked ? STATE_LOCK_SVG : STATE_PLAY_SVG}</span></a>`;
}
